/**
 * @file TemplateEditorToolbar.tsx
 * @description Панель инструментов визуального редактора шаблонов документов
 * (phase19.7): блоки, начертания, выравнивание, отступы, таблицы,
 * «Таблица позиций» и «Вставить поле».
 */

import type { Editor } from "@tiptap/core";
import {
  AlignCenter,
  AlignJustify,
  AlignLeft,
  AlignRight,
  Bold,
  Braces,
  IndentDecrease,
  IndentIncrease,
  Italic,
  Minus,
  Redo,
  Strikethrough,
  Table as TableIcon,
  Underline as UnderlineIcon,
  Undo,
} from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";

import { Button } from "../ui/button";
import { TEMPLATE_FIELDS } from "../../lib/template-editor-fields";
import { ITEMS_TABLE_HTML } from "../../lib/template-editor-transforms";

/**
 * Лёгкое локальное меню без порталов: base-ui DropdownMenu внутри
 * base-ui Dialog рендерится в портал и перекрывается инертным слоем
 * диалога (пункты некликабельны) — поэтому тулбар использует свой
 * absolute-поповер в потоке диалога.
 */
function ToolbarMenu({
  label,
  icon,
  children,
  widthClass = "w-56",
}: {
  label: string;
  icon: ReactNode;
  children: (close: () => void) => ReactNode;
  widthClass?: string;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  return (
    <div ref={rootRef} className="relative">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-8 px-2 text-xs"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {icon}
        {label}
      </Button>
      {open ? (
        <div
          className={`absolute left-0 top-full z-30 mt-1 max-h-80 overflow-y-auto rounded-md bg-surface-lowest p-1 shadow-lg [box-shadow:0_8px_24px_rgba(42,47,26,0.14),inset_0_0_0_1px_var(--outline-variant)] ${widthClass}`}
        >
          {children(() => setOpen(false))}
        </div>
      ) : null}
    </div>
  );
}

function MenuItem({
  onClick,
  disabled,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm text-on-surface hover:bg-surface-low disabled:opacity-40 disabled:pointer-events-none"
    >
      {children}
    </button>
  );
}

function ToolButton({
  onClick,
  active,
  disabled,
  aria,
  children,
}: {
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  aria: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={aria}
      title={aria}
      disabled={disabled}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={`rounded p-1.5 transition-colors hover:bg-surface-low disabled:opacity-40 disabled:pointer-events-none ${
        active ? "bg-surface-low text-primary" : "text-on-surface-variant"
      }`}
    >
      {children}
    </button>
  );
}

const Sep = () => <span className="mx-1 h-5 w-px bg-outline-variant/50" />;

export function TemplateEditorToolbar({ editor }: { editor: Editor }) {
  const blockValue = editor.isActive("heading", { level: 1 })
    ? "h1"
    : editor.isActive("heading", { level: 2 })
      ? "h2"
      : editor.isActive("heading", { level: 3 })
        ? "h3"
        : "p";

  const setBlock = (v: string) => {
    const chain = editor.chain().focus();
    if (v === "p") chain.setParagraph().run();
    else chain.toggleHeading({ level: Number(v.slice(1)) as 1 | 2 | 3 }).run();
  };

  const inTable = editor.isActive("table");

  return (
    <div className="flex flex-wrap items-center gap-0.5 border-b border-outline-variant/40 px-2 py-1.5 bg-surface-lowest rounded-t-md">
      <select
        aria-label="Блок"
        value={blockValue}
        onChange={(e) => setBlock(e.target.value)}
        className="h-8 rounded bg-input px-2 text-xs text-on-surface outline-none"
      >
        <option value="p">Абзац</option>
        <option value="h1">Заголовок 1</option>
        <option value="h2">Заголовок 2</option>
        <option value="h3">Заголовок 3</option>
      </select>
      <Sep />
      <ToolButton aria="Жирный" active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()}>
        <Bold className="size-4" />
      </ToolButton>
      <ToolButton aria="Курсив" active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}>
        <Italic className="size-4" />
      </ToolButton>
      <ToolButton aria="Подчёркнутый" active={editor.isActive("underline")} onClick={() => editor.chain().focus().toggleUnderline().run()}>
        <UnderlineIcon className="size-4" />
      </ToolButton>
      <ToolButton aria="Зачёркнутый" active={editor.isActive("strike")} onClick={() => editor.chain().focus().toggleStrike().run()}>
        <Strikethrough className="size-4" />
      </ToolButton>
      <Sep />
      <ToolButton aria="По левому краю" active={editor.isActive({ textAlign: "left" })} onClick={() => editor.chain().focus().setTextAlign("left").run()}>
        <AlignLeft className="size-4" />
      </ToolButton>
      <ToolButton aria="По центру" active={editor.isActive({ textAlign: "center" })} onClick={() => editor.chain().focus().setTextAlign("center").run()}>
        <AlignCenter className="size-4" />
      </ToolButton>
      <ToolButton aria="По правому краю" active={editor.isActive({ textAlign: "right" })} onClick={() => editor.chain().focus().setTextAlign("right").run()}>
        <AlignRight className="size-4" />
      </ToolButton>
      <ToolButton aria="По ширине" active={editor.isActive({ textAlign: "justify" })} onClick={() => editor.chain().focus().setTextAlign("justify").run()}>
        <AlignJustify className="size-4" />
      </ToolButton>
      <Sep />
      <ToolButton aria="Уменьшить отступ" onClick={() => editor.chain().focus().outdent().run()}>
        <IndentDecrease className="size-4" />
      </ToolButton>
      <ToolButton aria="Увеличить отступ" onClick={() => editor.chain().focus().indent().run()}>
        <IndentIncrease className="size-4" />
      </ToolButton>
      <Sep />
      {/* Таблицы */}
      <ToolbarMenu label="Таблица" icon={<TableIcon className="size-4" />}>
        {(close) => (
          <>
            <MenuItem
              onClick={() => {
                editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
                close();
              }}
            >
              Вставить таблицу 3×3
            </MenuItem>
            <MenuItem
              onClick={() => {
                editor.chain().focus().insertContent(ITEMS_TABLE_HTML).run();
                close();
              }}
            >
              Таблица позиций заказа
            </MenuItem>
            <MenuItem disabled={!inTable} onClick={() => { editor.chain().focus().addRowAfter().run(); close(); }}>
              Строка ниже
            </MenuItem>
            <MenuItem disabled={!inTable} onClick={() => { editor.chain().focus().deleteRow().run(); close(); }}>
              Удалить строку
            </MenuItem>
            <MenuItem disabled={!inTable} onClick={() => { editor.chain().focus().addColumnAfter().run(); close(); }}>
              Столбец справа
            </MenuItem>
            <MenuItem disabled={!inTable} onClick={() => { editor.chain().focus().deleteColumn().run(); close(); }}>
              Удалить столбец
            </MenuItem>
            <MenuItem disabled={!inTable} onClick={() => { editor.chain().focus().mergeCells().run(); close(); }}>
              Объединить ячейки
            </MenuItem>
            <MenuItem disabled={!inTable} onClick={() => { editor.chain().focus().splitCell().run(); close(); }}>
              Разделить ячейку
            </MenuItem>
            <MenuItem disabled={!inTable} onClick={() => { editor.chain().focus().deleteTable().run(); close(); }}>
              Удалить таблицу
            </MenuItem>
          </>
        )}
      </ToolbarMenu>
      {/* Поля Handlebars */}
      <ToolbarMenu label="Вставить поле" icon={<Braces className="size-4" />} widthClass="w-72">
        {(close) => (
          <>
            {TEMPLATE_FIELDS.map((group) => (
              <div key={group.group}>
                <div className="px-2 pt-2 pb-1 text-[10px] uppercase tracking-wider text-on-surface-muted">
                  {group.group}
                  {group.hint ? ` · ${group.hint}` : ""}
                </div>
                {group.items.map((f) => (
                  <MenuItem
                    key={f.path}
                    onClick={() => {
                      editor
                        .chain()
                        .focus()
                        .insertContent({ type: "placeholderChip", attrs: { path: f.path } })
                        .run();
                      close();
                    }}
                  >
                    {f.label}
                    <span className="ml-auto pl-3 text-[10px] text-on-surface-muted">
                      {`{{${f.path}}}`}
                    </span>
                  </MenuItem>
                ))}
              </div>
            ))}
          </>
        )}
      </ToolbarMenu>
      <ToolButton aria="Горизонтальная линия" onClick={() => editor.chain().focus().setHorizontalRule().run()}>
        <Minus className="size-4" />
      </ToolButton>
      <span className="ml-auto" />
      <ToolButton aria="Отменить" disabled={!editor.can().undo()} onClick={() => editor.chain().focus().undo().run()}>
        <Undo className="size-4" />
      </ToolButton>
      <ToolButton aria="Повторить" disabled={!editor.can().redo()} onClick={() => editor.chain().focus().redo().run()}>
        <Redo className="size-4" />
      </ToolButton>
    </div>
  );
}
